
//* Always check Firefox and Safari compatibility when implementing new features here
import { Calendar } from 'vue-sweet-calendar';
import router from '@/router';
import EazyHeading from '@/components/EazyHeading.vue';
import { getYYYYMMDDDate } from '@/logic/helpers/dates/getMeetingDateAndTime';
import store from '@/store';
import AssessmentLengthEstimation from '@/components/AssessmentLengthEstimation.vue';
import asyncOperations from './utils/asyncOperations';
import dateSelection from './utils/dateSelection';
import disableDates from './utils/disableDates';
import directUser from './utils/directUser';

export default {
  name: 'AppCalendar',

  components: {
    Calendar,
    EazyHeading,
    AssessmentLengthEstimation,
  },
  props: {
    pureCalendar: {
      type: Boolean,
      default: false,
    },
    padding: {
      type: String,
      default: '',
    },
    inModal: Boolean,
    candidateFromModal: {
      type: String,
      default: '',
    },
    companyFromModal: {
      type: String,
      default: '',
    },
    profilePicFromModal: {
      type: String,
      default: '',
    },
    positionFromModal: {
      type: String,
      default: '',
    },
    assessmentIdFromModal: {
      type: String,
      default: '',
    },
    assessorDashboardCalendar: {
      type: Boolean,
      default: false,
    },
    activeTrainingId: {
      type: String,
      default: '',
    },
    trainingCandidateId: {
      type: String,
      default: '',
    },
    calendarInDetailedView: {
      type: Boolean,
      default: false,
    },
  },
  data(): Record<string, unknown> {
    const yYear = new Date().getFullYear();
    const yMonth = new Date().getDate() <= 1 ? new Date().getMonth() : new Date().getMonth() + 1;

    // Additional logic to ensure correct day: If today is 2020-9-1, then yesterday was 2020-8-31, not 2020-9-0
    const yDay =
      new Date().getDate() - 1 >= 1
        ? new Date().getDate() - 1
        : new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate();
    const yesterday = `${yYear}-${yMonth < 10 ? '0' + yMonth : yMonth}-${
      yDay < 10 ? '0' + yDay : yDay
    }`;

    const pastProjectDeadline = this.getDateWithRequiredFormat(this.projectDeadline);

    return {
      router,
      device: store.state.device,
      processingGetTakenSlotsRequest: false,
      fetchPreferredWorkingDays: false,

      eventCategories: [
        { id: 1, title: 'Assessment', backgroundColor: '#2b89c3' },
        { id: 2, title: 'Past Date', backgroundColor: '#e4ece7' },
        { id: 3, title: 'Project Deadline', backgroundColor: '#707070' },
        { id: 4, title: 'Assessment and Deadline' },
        { id: 5, title: 'Selected Date', backgroundColor: '#2bc358' },
        { id: 6, title: 'Dates Past Deadline', backgroundColor: '#e4ece7' },
        { id: 7, title: 'Proposed Assessment', backgroundColor: '#41bbdc' },
        { id: 8, title: 'Proposed Assessment and Assessment' },
        { id: 9, title: 'Proposed Assessment and Deadline' },
        { id: 10, title: 'Training Event Deadline', backgroundColor: '#707070' },
      ],

      events: [
        {
          title: 'Past Dates',
          start: '1970-01-01',
          end: yesterday,
          repeat: 'never',
          categoryId: 2,
        },
        {
          title: 'Dates Past Deadline',
          start: pastProjectDeadline,
          end: '2999-12-31',
          repeat: 'never',
          categoryId: 6,
        },
      ],

      userRole: store.state.userRole,
      pickSingleDate: false,
      pickMultipleDates: false,
      selectedSingleDate: '',
      selectedDates: [],
      selectedBusyDates: [],
      hoursBottomRange: '',
      hoursTopRange: '',
      fetchDeadlineForProject: false,
      projectDeadline: '',
      yesterday: yesterday,
      dateTypeChange: false,
      calendarAvailable: false,
      pureCalendarForMobile: this.$route.query.pureCalendarForMobile,
    };
  },

  computed: {
    isTrainingView(): boolean {
      return this.$route.path === '/candidate-trainings';
    },
    isClientTrainingView(): boolean {
      return this.$route.path === '/client-trainings';
    },
    calendarLoading(): boolean {
      return (
        this.processingGetTakenSlotsRequest || this.fetchPreferredWorkingDays || this.dateTypeChange
      );
    },

    calendarExists(): boolean {
      return !!document.querySelector('#sweetCalendar');
    },

    userIsOnDesktopOrAssessorPickedDateType(): boolean {
      if (
        (!this.isPureCalendar || this.isInModal) &&
        !this.pickSingleDate &&
        !this.pickMultipleDates
      )
        return false;
      return true;
    },

    userSelectedAtLeastTwoDates(): boolean {
      return (
        (!this.isPureCalendar || this.isInModal) &&
        this.pickMultipleDates &&
        this.selectedDates.length > 1
      );
    },

    isInModal(): boolean {
      if (typeof this.$route.query.inModal === 'string') return false;
      return this.$route.query.inModal || this.inModal;
    },

    candidate(): string {
      return this.$route.query.cand || this.candidateFromModal || 'name_surname';
    },

    candidateId(): string {
      return this.$route.query.candID || this.candidateIdFromModal;
    },

    company(): string {
      return this.$route.query.comp || this.companyFromModal;
    },

    position(): string {
      return this.$route.query.pos || this.positionFromModal;
    },

    profilePic(): string {
      return this.$route.query.pic || this.profilePicFromModal;
    },

    assessmentId(): string {
      return this.$route.query.assessmentID || this.assessmentIdFromModal;
    },

    isPureCalendar(): boolean {
      return this.pureCalendar || this.$route.query.pureCalendar === 'true';
    },

    isAssessorDashboardCalendar(): boolean {
      return (
        this.assessorDashboardCalendar || this.$route.query.assessorDashboardCalendar === 'true'
      );
    },
  },

  watch: {
    pickMultipleDates(): void {
      this.dateTypeChange = true;
      dateSelection.calRef = this;

      if (this.pickMultipleDates) {
        document
          .querySelector('#sweetCalendar .body')
          .removeEventListener('click', directUser.goFromCalendarToCertainView);
        document
          .querySelector('#sweetCalendar .body')
          .addEventListener('click', dateSelection.selectMultipleDates);
      } else {
        document
          .querySelector('#sweetCalendar .body')
          .addEventListener('click', directUser.goFromCalendarToCertainView);
        document
          .querySelector('#sweetCalendar .body')
          .removeEventListener('click', dateSelection.selectMultipleDates);
      }

      this.waitForCalendarToProcessModeChange();
    },

    activeTrainingId: 'adjustCalendarStyles',

    pickSingleDate(): void {
      this.dateTypeChange = true;
      dateSelection.calRef = this;

      this.selectedDates = [];
      this.selectedBusyDates = [];
      dateSelection.clearPreviouslySelectedDates();

      if (this.selectedSingleDate && this.pickSingleDate) {
        const selectedDay: HTMLElement = document.querySelector(
          '[data-selected-single-date]',
        ) as HTMLElement;
        const possibleDate = this.getDateWithRequiredFormat(
          this.getPickedDate((selectedDay.firstChild as HTMLElement).innerText),
        );

        if (!selectedDay || this.selectedSingleDate !== possibleDate) {
          this.waitForCalendarToProcessModeChange();
          return;
        }

        selectedDay.style.background = 'none';
        selectedDay.style.backgroundColor = '#2bc358';
        (selectedDay.firstChild as HTMLElement).style.color = '#fff';
      }

      this.waitForCalendarToProcessModeChange();
    },
    userSelectedAtLeastTwoDates(): void {
      if (this.userSelectedAtLeastTwoDates && this.inModal) {
        setTimeout(() => {
          document.querySelector('#proceedButton').scrollIntoView();
        }, 200);
      }
    },

    selectedDates(): void {
      if (this.isInModal) this.$emit('multipleDatesPicked', this.selectedDates);
    },
  },

  created(): void {
    asyncOperations.getProject(this);
  },

  mounted(): void {
    this.adjustCalendarStyles();
    this.registerListenersForCalendar();
    asyncOperations.getPreferredWorkingDays(this);
  },

  methods: {
    handleProceedAction(): void {
      directUser.goDirectlyToAssessmentPlaceWithMultipleDates(this);
    },

    waitForCalendarToProcessModeChange(): void {
      setTimeout(() => {
        this.dateTypeChange = false;
      }, 1000);
    },

    handleDateOptions(dateType: string): void {
      if (dateType === 'pickSingleDate')
        (this.pickSingleDate = true), (this.pickMultipleDates = false);
      if (dateType === 'pickMultipleDates')
        (this.pickMultipleDates = true), (this.pickSingleDate = false);

      this.$emit('dateOptionsChange', dateType);
    },

    goToday(): void {
      this.$refs.calendar.goToday();
    },

    adjustCalendarStyles(projectDeadlineFetched: boolean): void {
      this.cleanEvents();
      if (
        (!this.isPureCalendar && !projectDeadlineFetched) ||
        (this.isInModal && !projectDeadlineFetched)
      )
        return;

      this.cleanDays();
      dateSelection.clearPreviouslySelectedDates();
      asyncOperations.getBusyDays(this);
      if (this.userRole === 'client' && !this.isClientTrainingView) {
        asyncOperations.getDeadlines(this);
        disableDates.pastDatesAndDatesAfterProjDeadline(this);
      } else if (this.userRole === 'client' && this.isClientTrainingView) {
        asyncOperations.getClientDeadlinesForTrainings(this, this.activeTrainingId);
      }
      if (this.isTrainingView && this.userRole === 'candidate') {
        asyncOperations.getDeadlinesForTrainings(
          this,
          this.activeTrainingId,
          this.trainingCandidateId,
        );
      }
      if (this.userRole === 'candidate' && !this.calendarInDetailedView) {
        asyncOperations.getDeadlinesForTrainings(this);
      }

      if (!this.isAssessorDashboardCalendar) dateSelection.applySelectedDates(this);

      this.calendarAvailable = true;
    },

    isDayWithVariousEvents(title: string): boolean {
      return (
        title === this.$t('numOfAssessmentsThisDay3') ||
        title === this.$t('eventsThisDay1') ||
        title === this.$t('eventsThisDay2')
      );
    },

    cleanEvents(): void {
      this.events = [];
    },

    cleanDays(): void {
      const allDaysEls = document.querySelectorAll('#sweetCalendar .day');

      allDaysEls.forEach((day: HTMLElement) => {
        const meetingsCount = day.querySelector('.calendar__meetings-count');
        const deadlinesCount = day.querySelector('.calendar__deadlines-count');
        const variousEventsCount = day.querySelector('.calendar__various-events-count');
        const proposedMeetingsCount = day.querySelector('.calendar__proposed-meetings-count');
        const dayWithVariousEvents = this.isDayWithVariousEvents(`${day.title.split(':')[0]}:`);
        const hoverTooltip = day.querySelector('.day__hover-tooltip');

        day.removeAttribute('data-is-busy');
        day.removeAttribute('title');
        (day.firstChild as HTMLElement).style.color = '#707070';

        if (dayWithVariousEvents) day.style.background = 'none';
        if (day.contains(meetingsCount)) day.removeChild(meetingsCount);
        if (day.contains(deadlinesCount)) day.removeChild(deadlinesCount);
        if (day.contains(variousEventsCount)) day.removeChild(variousEventsCount);
        if (day.contains(proposedMeetingsCount)) day.removeChild(proposedMeetingsCount);
        if (day.contains(hoverTooltip)) day.removeChild(hoverTooltip);
        if (day.hasAttribute('project')) day.removeAttribute('project');
      });
    },

    getPickedDate(day: string): string {
      const monthAndYear = (document.querySelector('#sweetCalendar .month') as HTMLElement)
        .innerText;
      return `${day} ${monthAndYear}`;
    },

    registerListenersForCalendar(): void {
      directUser.calRef = this;

      if (this.isAssessorDashboardCalendar) {
        if (!document.querySelector('.assessor-dashboard-calendar')) return;

        document
          .querySelector('.assessor-dashboard-calendar')
          .addEventListener('click', directUser.goFromCalendarToCertainView);
        document
          .querySelector('.assessor-dashboard-calendar .left-arrow')
          .addEventListener('click', this.adjustCalendarStyles);
        document
          .querySelector('.assessor-dashboard-calendar .right-arrow')
          .addEventListener('click', this.adjustCalendarStyles);
        document.querySelectorAll('.assessor-dashboard-calendar .day').forEach((day) => {
          day.addEventListener('mouseover', this.adjustEventNums);
          day.addEventListener('mouseleave', this.adjustEventNums);
        });
      } else {
        if (!document.querySelector('#sweetCalendar')) return;

        document
          .querySelector('#sweetCalendar .body')
          .addEventListener('click', directUser.goFromCalendarToCertainView);
        document
          .querySelector('#sweetCalendar .left-arrow')
          .addEventListener('click', this.adjustCalendarStyles);
        document
          .querySelector('#sweetCalendar .right-arrow')
          .addEventListener('click', this.adjustCalendarStyles);
        document.querySelectorAll('#sweetCalendar .day').forEach((day) => {
          day.addEventListener('mouseover', this.adjustEventNums);
          day.addEventListener('mouseleave', this.adjustEventNums);
        });
      }
    },

    adjustEventNums(e: { type: string; target: { querySelector: (arg0: string) => any } }): void {
      const allEventNums = [
        ...document.querySelectorAll('.calendar__meetings-count'),
        ...document.querySelectorAll('.calendar__deadlines-count'),
        ...document.querySelectorAll('.calendar__various-events-count'),
        ...document.querySelectorAll('.calendar__proposed-meetings-count'),
      ];

      const display = e.type === 'mouseover' ? 'none' : 'block';
      const tooltip = e.target.querySelector('.day__hover-tooltip');

      if (tooltip)
        allEventNums.forEach((notification: HTMLElement) => (notification.style.display = display));
    },

    extractInfoFromElementAttribute(
      event: {
        target: {
          getAttribute: (arg0: any) => any;
          parentNode: { getAttribute: (arg0: any) => any };
        };
      },
      attribute: Record<string, any>,
      operation: (arg0: any) => any,
      returnType: string,
    ): string | number {
      const clickTargetAttr = event.target.getAttribute(attribute);
      const clickTargetParentNodeAttr = event.target.parentNode.getAttribute(attribute);

      if (!clickTargetAttr && !clickTargetParentNodeAttr) return;

      const info = clickTargetAttr
        ? operation(clickTargetAttr)
        : operation(clickTargetParentNodeAttr);

      if (returnType === 'number') return parseInt(info);
      return info;
    },

    getDateWithRequiredFormat(date: string | number | Date): string {
      const dateObj = new Date(date);
      const month = dateObj.getMonth() + 1;
      const day = dateObj.getDate();

      const finalDate = `${dateObj.getFullYear()}-${month < 10 ? '0' + month : month}-${
        day < 10 ? '0' + day : day
      }`;

      return finalDate;
    },

    getDateRangeForCurrentMonth(): { dateFrom: string; dateTo: string } {
      if (!this.calendarExists) return;

      let dateFrom, dateTo;

      const addIfCalendarIsOnAssessorDash = this.isAssessorDashboardCalendar
        ? '.assessor-dashboard-calendar'
        : '';
      const monthAndYear = (document.querySelector(
        `#sweetCalendar${addIfCalendarIsOnAssessorDash} .month`,
      ) as HTMLElement)?.innerText;

      // Adding '1' prevents Firefox bugs.
      const date = new Date(`1 ${monthAndYear}`);

      dateFrom = getYYYYMMDDDate(new Date(date.getFullYear(), date.getMonth()).toString());
      dateTo = getYYYYMMDDDate(new Date(date.getFullYear(), date.getMonth() + 1, 0).toString());

      return {
        dateFrom,
        dateTo,
      };
    },
  },
};
